import { Badge, Box, Group, Grid, Paper, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { CLINICAL_RISK_COLOR } from "@constants/resultColors";
import { useTranslation } from "react-i18next";
import { OcularMotilityData } from "../hooks/useNeurologicalData";
import CircularPursuitGraph from "../../testContainer/common/CircularPursuitGraph";
import {
  ClinicalSummaryMetric,
  ClinicalRiskLevel,
} from "../../testContainer/result/utils/clinicalSummaryTypes";
import { interpretOcularMotilitySummary } from "../../testContainer/result/utils/ocularMotilityInterpretation";

interface SimplifiedOcularMotilityCardProps {
  data: OcularMotilityData;
}

const formatMetricValue = (metric?: ClinicalSummaryMetric) => {
  if (!metric || metric.value === null || metric.value === undefined || metric.value === "") {
    return "—";
  }

  if (typeof metric.value === "string") {
    return metric.value;
  }

  const formattedValue =
    typeof metric.decimals === "number" ? metric.value.toFixed(metric.decimals) : `${metric.value}`;
  return `${formattedValue}${metric.unit ?? ""}`;
};

const getOverviewSummary = (riskLevel: ClinicalRiskLevel, metrics: ClinicalSummaryMetric[]) => {
  const errorMetric = metrics.find((metric) => metric.labelKey.endsWith("worstTrackingError"));
  const gainMetric = metrics.find((metric) => metric.labelKey.endsWith("worstPursuitGain"));

  const errorValue = formatMetricValue(errorMetric);
  const gainValue = formatMetricValue(gainMetric);

  switch (riskLevel) {
    case "high":
      return {
        headline: "Eye tracking looked clearly uneven on this test.",
        detail: `The weaker tracking score was ${gainValue} and the tracking error reached ${errorValue}.`,
      };
    case "monitor":
      return {
        headline: "Eye tracking was a bit uneven on this test.",
        detail: `The weaker tracking score was ${gainValue} and the error stayed around ${errorValue}.`,
      };
    default:
      return {
        headline: "Eye tracking looked steady and fairly even on this test.",
        detail: `The weaker tracking score was ${gainValue} with tracking error around ${errorValue}.`,
      };
  }
};

export const SimplifiedOcularMotilityCard = ({ data }: SimplifiedOcularMotilityCardProps) => {
  const { t } = useTranslation();
  const interpretation = interpretOcularMotilitySummary({
    chartData: data.chartData,
    metrics: data.metrics,
    testType: data.testType,
  });

  const hasData = Boolean(data.chartData?.length && data.metrics && interpretation);

  if (!hasData || !interpretation) {
    return (
      <Paper withBorder p="md" radius="md">
        <Stack align="center" gap="xs" py="xl">
          <IconAlertCircle size={28} color="var(--mantine-color-gray-6)" />
          <Text c="dimmed" size="sm" ta="center">
            No eye tracking test data available
          </Text>
          <Text c="dimmed" size="xs" ta="center">
            Complete an ocular motility test to see your results here
          </Text>
        </Stack>
      </Paper>
    );
  }

  const overviewSummary = getOverviewSummary(interpretation.riskLevel, interpretation.metrics);

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ flex: 1 }}>
            <Title order={5}>{t(interpretation.titleKey)}</Title>
            <Text size="sm" c="dimmed">
              {t(interpretation.explainerKey)}
            </Text>
          </Stack>
          <Badge color={CLINICAL_RISK_COLOR[interpretation.riskLevel]} variant="filled" size="lg">
            {t(`extraTests.ocularMotility.summary.risk.${interpretation.riskLevel}`)}
          </Badge>
        </Group>

        <Box bg="var(--oculera-surface-alt)" p="sm" style={{ borderRadius: "10px" }}>
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              {overviewSummary.headline}
            </Text>
            <Text size="sm" c="dimmed">
              {overviewSummary.detail}
            </Text>
          </Stack>
        </Box>

        <Grid gutter="sm" align="stretch" className="print-row-4-4-4">
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Box p="xs" pos="relative" style={{ borderRadius: "10px", height: "100%" }}>
              <Text size="xs" fw={600} pos="absolute" style={{ top: 8, right: 8, zIndex: 1 }}>
                {t("leftEye")}
              </Text>
              <Box
                style={{
                  width: "100%",
                  maxWidth: 240,
                  aspectRatio: "1 / 1",
                  margin: "0 auto",
                }}
              >
                <CircularPursuitGraph
                  data={data.chartData}
                  visibleEye="left"
                  compact
                  height="100%"
                  showPaper={false}
                />
              </Box>
            </Box>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Box p="xs" pos="relative" style={{ borderRadius: "10px", height: "100%" }}>
              <Text size="xs" fw={600} pos="absolute" style={{ top: 8, right: 8, zIndex: 1 }}>
                {t("rightEye")}
              </Text>
              <Box
                style={{
                  width: "100%",
                  maxWidth: 240,
                  aspectRatio: "1 / 1",
                  margin: "0 auto",
                }}
              >
                <CircularPursuitGraph
                  data={data.chartData}
                  visibleEye="right"
                  compact
                  height="100%"
                  showPaper={false}
                />
              </Box>
            </Box>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Box p="xs" style={{ borderRadius: "10px", height: "100%" }}>
              <SimpleGrid cols={1} spacing="xs">
                {interpretation.metrics.map((metric) => (
                  <Stack key={metric.labelKey} gap={0}>
                    <Text
                      size="xs"
                      c="dimmed"
                      tt="uppercase"
                      fw={600}
                      style={{
                        lineHeight: 1.2,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      {t(metric.labelKey)}
                    </Text>
                    <Text fw={700} size="md" style={{ lineHeight: 1.15 }}>
                      {formatMetricValue(metric)}
                    </Text>
                  </Stack>
                ))}
              </SimpleGrid>
            </Box>
          </Grid.Col>
        </Grid>
      </Stack>
    </Paper>
  );
};
